import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Building2, MapPin, Users, CheckCircle2 } from 'lucide-react';
import { PTPDocument } from './RiskAreasWidget';
import { useEscapeKey } from '../hooks/useEscapeKey';
interface PTPDocumentViewerProps {
  document: PTPDocument | null;
  onClose: () => void;
}
export function PTPDocumentViewer({
  document,
  onClose
}: PTPDocumentViewerProps) {
  useEscapeKey(onClose, !!document);
  if (!document) return null;
  const engagementRate = Math.round(document.signatureCount / document.totalWorkers * 100);
  const isLowEngagement = engagementRate < 70;
  return <AnimatePresence data-id="element-3022">
      {document && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3023">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-3024" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.5,
        bounce: 0.3
      }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-3025">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-3026">
              <div data-id="element-3027">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-3028">
                  Pre-Task Plan
                </h3>
                <p className="text-sm text-gray-500" data-id="element-3029">
                  Safety documentation review
                </p>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-3030">
                <X size={20} data-id="element-3031" />
              </button>
            </div>

            {/* Document Info */}
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100" data-id="element-3032">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3033">
                <div className="flex items-center gap-2" data-id="element-3034">
                  <Calendar size={16} className="text-blue-600" data-id="element-3035" />
                  <div data-id="element-3036">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3037">Date</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3038">
                      {document.date}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3039">
                  <Building2 size={16} className="text-blue-600" data-id="element-3040" />
                  <div data-id="element-3041">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3042">Company</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3043">
                      {document.company}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3044">
                  <MapPin size={16} className="text-blue-600" data-id="element-3045" />
                  <div data-id="element-3046">
                    <p className="text-xs text-blue-600 font-medium" data-id="element-3047">
                      Location
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3048">
                      {document.location}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-3049">
                  <Users size={16} className={isLowEngagement ? 'text-amber-600' : 'text-emerald-600'} data-id="element-3050" />
                  <div data-id="element-3051">
                    <p className="text-xs font-medium" style={{
                  color: isLowEngagement ? '#d97706' : '#059669'
                }} data-id="element-3052">
                      Signatures
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3053">
                      {document.signatureCount} of {document.totalWorkers} (
                      {engagementRate}%)
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200" data-id="element-3054">
                <p className="text-xs text-gray-600 font-medium mb-1" data-id="element-3055">
                  Activity
                </p>
                <p className="text-sm text-gray-900" data-id="element-3056">{document.activity}</p>
              </div>

              {isLowEngagement && <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-id="element-3057">
                  <Users size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3058" />
                  <div data-id="element-3059">
                    <p className="text-xs font-semibold text-amber-900" data-id="element-3060">
                      Low Engagement Alert
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3061">
                      Only {engagementRate}% of workers signed this PTP. Target
                      is 90%+.
                    </p>
                  </div>
                </div>}
            </div>

            {/* Document Images */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50" data-id="element-3062">
              <div className="space-y-4" data-id="element-3063">
                {document.imageUrls.map((url, index) => <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200" data-id="element-3064">
                    <div className="px-4 py-2 bg-gray-100 border-b border-gray-200" data-id="element-3065">
                      <p className="text-xs font-semibold text-gray-600" data-id="element-3066">
                        Page {index + 1} of {document.imageUrls.length}
                      </p>
                    </div>
                    <img src={url} alt={`PTP Document Page ${index + 1}`} className="w-full h-auto" data-id="element-3067" />
                  </div>)}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-3068">
              <button onClick={onClose} className="w-full py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors" data-id="element-3069">
                Close
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}